import React, { useEffect } from 'react';
import { Alert, Box, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import type { SystemPreflightConfig, SystemPreflightDefinition } from '../../types';

interface Props {
  definition?: SystemPreflightDefinition;
  value?: SystemPreflightConfig;
  onChange: (value: SystemPreflightConfig) => void;
  onValidityChange?: (valid: boolean) => void;
}

export default function SystemPreflightSettings({ definition, value, onChange, onValidityChange }: Props) {
  const conditions = definition?.conditions || [];
  const parameters = (key: string) => ({
    ...definition?.configuration.conditions[key], ...value?.conditions?.[key],
  });
  const invalid = conditions.some(condition => Object.entries(condition.settings_schema.properties).some(([key, schema]) => {
    if (!['number', 'integer'].includes(schema.type)) return false;
    const current = parameters(condition.condition_id)[key] ?? 0;
    return typeof current !== 'number' || !Number.isFinite(current) || current < 0
      || (schema.maximum !== undefined && current > schema.maximum)
      || (schema.type === 'integer' && !Number.isInteger(current));
  }));
  useEffect(() => { onValidityChange?.(!invalid); }, [invalid, onValidityChange]);
  const update = (condition: string, key: string, next: number | boolean | string) => onChange({
    version: definition?.version,
    conditions: {
      ...definition?.configuration.conditions, ...value?.conditions,
      [condition]: { ...parameters(condition), [key]: next },
    },
  });
  return <Stack spacing={1.5}>
    <Typography variant="subtitle2">Built-in preflight</Typography>
    <Typography variant="body2" color="text.secondary">
      Runs before the expert workflow. Zero or off disables a check. With all checks disabled, preflight takes no action.
    </Typography>
    {!definition && <Typography variant="body2">All checks start disabled. Save the system to configure its limits.</Typography>}
    {invalid && <Alert severity="error">Use non-negative limits within the displayed range. Position and loss counts must be whole numbers.</Alert>}
    {conditions.map(condition => <Box key={condition.condition_id}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>{condition.label}</Typography>
      <Stack spacing={1}>
        {Object.entries(condition.settings_schema.properties).map(([key, schema]) => schema.type === 'boolean'
          ? <FormControlLabel key={key} label={`Enable ${condition.label}`} control={<Switch size="small"
              checked={parameters(condition.condition_id)[key] === true}
              onChange={event => update(condition.condition_id, key, event.target.checked)} />} />
          : <TextField key={key} size="small" fullWidth
              label={key.replace(/_/g, ' ')}
              type={schema.type === 'string' ? 'text' : 'number'}
              value={parameters(condition.condition_id)[key] ?? (schema.type === 'string' ? '' : 0)}
              onChange={event => update(condition.condition_id, key, schema.type === 'string' ? event.target.value : Number(event.target.value))}
              inputProps={schema.type === 'string' ? undefined : { min: 0, max: schema.maximum, step: schema.type === 'integer' ? 1 : 'any' }}
              helperText={schema.type === 'string' ? schema.description : `0 disables this check${schema.maximum !== undefined ? `; maximum ${schema.maximum}` : ''}.`} />)}
      </Stack>
    </Box>)}
  </Stack>;
}
